import React, { useEffect, useRef, useState } from 'react';
import { X, Mail, Lock, Eye, EyeOff, User, Phone, MapPin, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './AuthModal.css';

// step: 'login' | 'register' | 'profile'
export default function AuthModal({ isOpen, onClose, initialStep = 'login' }) {
  const { login, register, updateProfile, isProfileComplete, user } = useAuth();

  const [step, setStep]       = useState(initialStep);
  const [form, setForm]       = useState({ email: '', password: '', name: '', phone: '', address: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const overlayRef = useRef();

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
      setStep(initialStep);
      setForm({ email: '', password: '', name: '', phone: '', address: '' });
    }
  }, [isOpen, initialStep]);

  // If user just logged in but profile incomplete → go to profile step
  useEffect(() => {
    if (isOpen && user && !isProfileComplete) setStep('profile');
  }, [user, isProfileComplete, isOpen]);

  if (!isOpen) return null;

  const set = (key) => (e) => {
    setError('');
    setForm(prev => ({ ...prev, [key]: e.target.value }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = await login(form.email.trim(), form.password);
      if (!u.name || !u.phone || !u.address) {
        setStep('profile');
      } else {
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setError('');
    setLoading(true);
    try {
      await register(form.email.trim(), form.password);
      setStep('profile');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())    { setError('Name is required.'); return; }
    if (!form.phone.trim())   { setError('Phone number is required.'); return; }
    if (!form.address.trim()) { setError('Delivery address is required.'); return; }
    setError('');
    setLoading(true);
    try {
      await updateProfile({ name: form.name.trim(), phone: form.phone.trim(), address: form.address.trim() });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="auth-modal" role="dialog" aria-modal="true">
        {/* Close */}
        <button className="auth-close" onClick={onClose} aria-label="Close"><X size={18} /></button>

        {/* Steps indicator */}
        <div className="auth-steps">
          <div className={`auth-step-dot ${step === 'login' || step === 'register' ? 'active' : 'done'}`}>1</div>
          <div className="auth-step-line" />
          <div className={`auth-step-dot ${step === 'profile' ? 'active' : (user && isProfileComplete ? 'done' : '')}`}>2</div>
        </div>

        {/* ── Login / Register ──────────────────────────────────────── */}
        {(step === 'login' || step === 'register') && (
          <>
            <div className="auth-header">
              <div className="auth-icon-wrap">
                <Lock size={22} />
              </div>
              <h2>{step === 'login' ? 'Welcome back' : 'Create account'}</h2>
              <p>{step === 'login' ? 'Sign in to add items to your cart.' : 'Register to start ordering healthcare linens.'}</p>
            </div>

            <div className="auth-tabs">
              <button className={step === 'login'    ? 'active' : ''} onClick={() => { setStep('login');    setError(''); }}>Sign In</button>
              <button className={step === 'register' ? 'active' : ''} onClick={() => { setStep('register'); setError(''); }}>Register</button>
            </div>

            <form onSubmit={step === 'login' ? handleLoginSubmit : handleRegisterSubmit}>
              <div className="auth-field">
                <label htmlFor="auth-email">Email address</label>
                <div className="auth-input-wrap">
                  <Mail size={15} className="auth-input-icon" />
                  <input id="auth-email" type="email" placeholder="you@facility.com" value={form.email} onChange={set('email')} required autoComplete="email" />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="auth-pass">Password</label>
                <div className="auth-input-wrap">
                  <Lock size={15} className="auth-input-icon" />
                  <input id="auth-pass" type={showPass ? 'text' : 'password'} placeholder={step === 'register' ? 'Min 6 characters' : '••••••••'} value={form.password} onChange={set('password')} required autoComplete={step === 'login' ? 'current-password' : 'new-password'} />
                  <button type="button" className="auth-eye" onClick={() => setShowPass(v => !v)} aria-label="Toggle password">{showPass ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                </div>
              </div>

              {error && <p className="auth-error">{error}</p>}

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? <span className="auth-spinner" /> : (step === 'login' ? 'Sign In' : 'Create Account')}
              </button>
            </form>
          </>
        )}

        {/* ── Profile Completion ───────────────────────────────────── */}
        {step === 'profile' && (
          <>
            <div className="auth-header">
              <div className="auth-icon-wrap success">
                <CheckCircle size={22} />
              </div>
              <h2>Complete your profile</h2>
              <p>We need your delivery details to process orders.</p>
            </div>

            <form onSubmit={handleProfileSubmit}>
              <div className="auth-field">
                <label htmlFor="auth-name">Full name</label>
                <div className="auth-input-wrap">
                  <User size={15} className="auth-input-icon" />
                  <input id="auth-name" type="text" placeholder="Jane Smith" value={form.name} onChange={set('name')} required />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="auth-phone">Phone number</label>
                <div className="auth-input-wrap">
                  <Phone size={15} className="auth-input-icon" />
                  <input id="auth-phone" type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={set('phone')} required />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="auth-addr">Delivery address</label>
                <div className="auth-input-wrap">
                  <MapPin size={15} className="auth-input-icon" />
                  <textarea id="auth-addr" placeholder="123 Hospital Dr, City, State, ZIP" value={form.address} onChange={set('address')} rows={3} required />
                </div>
              </div>

              {error && <p className="auth-error">{error}</p>}

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? <span className="auth-spinner" /> : 'Save & Continue'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
